import React, { useCallback, useEffect, useRef, useState } from 'react';
import { token } from '@atlaskit/tokens';
import type {
	SelectOption,
	FormatOptionLabel,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { isSingleValueOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/utils/is-single-value-option.tsx';
import formatOptionLabelDefault from '@atlassian/jira-jql-builder-basic-picker/src/ui/format-option-label/index.tsx';
import { useSynchronisedAnimation } from './utils.tsx';

const ANIMATION_INTERVAL_MS = 1000;

type Props = {
	data: SelectOption;
	isShimmering: boolean;
	onAnimateStart: (el: HTMLElement) => void;
	onAnimateEnd: (el: HTMLElement) => void;
};

export const ShimmeringLabel = ({ data, isShimmering, onAnimateStart, onAnimateEnd }: Props) => {
	const element = useRef(null);

	useEffect(() => {
		if (element.current && isShimmering) {
			const el = element.current;
			onAnimateStart(el);
			// Clean up animations when the component is unmounted or isShimmering changes
			return () => onAnimateEnd(el);
		}
		return undefined;
	}, [isShimmering, onAnimateEnd, onAnimateStart]);

	return <span ref={element}>{formatOptionLabelDefault(data)}</span>;
};

export const useFormatOptionLabelWithShimmer = (
	nonCommittedFieldSetIds: Set<string>,
): FormatOptionLabel => {
	// We use state instead of constructing the object at the module level to prevent errors being thrown during tests
	const [shimmerEffect] = useState(() =>
		// KeyframeEffect is available in all supported browsers but we'll check anyway so the app doesn't crash.

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.KeyframeEffect
			? new KeyframeEffect(
					null,
					[
						{ color: token('color.text') },
						{ color: token('color.text.disabled') },
						{ color: token('color.text') },
					],
					{ duration: ANIMATION_INTERVAL_MS },
				)
			: null,
	);
	const { onAnimateStart, onAnimateEnd } = useSynchronisedAnimation(
		shimmerEffect,
		ANIMATION_INTERVAL_MS,
	);

	return useCallback(
		(data) =>
			isSingleValueOption(data) ? (
				<ShimmeringLabel
					data={data}
					isShimmering={nonCommittedFieldSetIds.has(data.value)}
					onAnimateStart={onAnimateStart}
					onAnimateEnd={onAnimateEnd}
				/>
			) : null,
		[nonCommittedFieldSetIds, onAnimateEnd, onAnimateStart],
	);
};
